import { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  DropDown,
  FlexColumn,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  TextField,
  ToggleButton as ToggleButtonBase,
  Dialog,
} from '@elementary/common-ui/components';
import { TrackingContext } from '@elementary/common-ui/providers/tracking';
import { ToggleButtonGroup } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ReactComponent as ThumbsUpIcon } from '@elementary/common-ui/assets/16x16/thumbs-up.svg';
import { space } from '@elementary/common-ui/style/utils';

const Content = styled(FlexColumn)`
  gap: ${space('lg')};
`;

const ToggleButton = styled(ToggleButtonBase)`
  color: ${({ theme }) => theme.palette.content.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};

  &.Mui-selected {
    background: ${({ theme }) => theme.palette.surface.selected};
    color: ${({ theme }) => theme.palette.accent.secondary};
  }
  &:hover {
    background: ${({ theme }) => theme.palette.surface.selected} !important;
  }
`;

type FeedbackForm = {
  kind: 'falsePositive' | 'truePositive';
  reason: string;
  additionalContext?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  testMetadata: {
    testUniqueId: string;
    latestRunTime?: string;
    latestRunStatus?: string;
  };
};

const FeedbackDialog = ({ open, onClose, testMetadata }: Props) => {
  const methods = useForm<FeedbackForm>();

  const tracking = useContext(TrackingContext);
  const trackingEvents = tracking?.analytics.events;

  const reasonOptions = useMemo(() => {
    if (methods.watch('kind') === 'falsePositive') {
      return [
        'Insignificant change',
        'Expected outlier',
        'Business anomaly',
        'Not an interesting table',
        'Other',
      ];
    }
    if (methods.watch('kind') === 'truePositive') {
      return [
        'Recent unexpected bug',
        'Source issue',
        'Pipeline didn’t run properly',
      ];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch('kind')]);

  useEffect(() => {
    if (open) {
      methods.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = (data: FeedbackForm) => {
    tracking?.sendTrackingEvent({
      eventName: trackingEvents?.SUBMIT_TEST_FEEDBACK,
      properties: {
        test_id: testMetadata.testUniqueId,
        latest_run_time: testMetadata.latestRunTime,
        latest_run_status: testMetadata.latestRunStatus,
        feedback: {
          kind: data.kind,
          reason: data.reason,
          additional_context: data.additionalContext,
        },
      },
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      confirmDisabled={!methods.formState.isValid}
      cancelText="Close"
      confirmText="Submit"
      title="Result Feedback"
      titleIcon={<ThumbsUpIcon />}
      onConfirmButtonClick={methods.handleSubmit(onSubmit)}
      message={
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Content>
            <Controller
              control={methods.control}
              name="kind"
              rules={{ required: requiredLabel }}
              render={({ field }) => (
                <ToggleButtonGroup
                  {...field}
                  fullWidth
                  exclusive
                  onChange={(e: any, val) => {
                    if (!val) {
                      methods.resetField('kind');
                    } else {
                      field.onChange(e);
                    }
                  }}
                >
                  <ToggleButton value="falsePositive">
                    False positive
                  </ToggleButton>
                  <ToggleButton value="truePositive">
                    True positive
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />

            <Controller
              name="reason"
              control={methods.control}
              rules={{ required: requiredLabel }}
              disabled={!methods.watch('kind')}
              render={({ field }) => (
                <DropDown
                  size="large"
                  placeholder="Choose ranking reason"
                  options={reasonOptions.map((o) => ({ label: o, value: o }))}
                  fullWidth
                  {...field}
                />
              )}
            />

            <FormField
              title="Additional context"
              optional
              errorMessage={methods.formState.errors.additionalContext?.message}
            >
              <TextField
                {...methods.register('additionalContext')}
                sx={{
                  '& .MuiInputBase-root': {
                    padding: (theme) =>
                      `${theme.tokens.space.sm} ${theme.tokens.space.lg} !important`,
                  },
                }}
                variant="outlined"
                multiline
                maxRows={3}
                rows={3}
              />
            </FormField>
          </Content>
        </form>
      }
    />
  );
};

export default FeedbackDialog;
